import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Loader2, TrendingUp, FileCheck, Search, Download, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useChantiersRealtime } from "@/hooks/useChantiersRealtime";
import ExportManager from "@/components/ExportManager";
import EmptyState from "@/components/EmptyState";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { labels, emptyStates } from "@/lib/content";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";

const Reports = () => {
  const [chantiers, setChantiers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadChantiers();
  }, []);

  // Setup Realtime subscriptions
  const handleRealtimeChange = useCallback(() => {
    loadChantiers();
  }, []);

  useChantiersRealtime(entrepriseId, handleRealtimeChange);

  const loadChantiers = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: entreprise } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user.id)
        .single();

      if (!entreprise) return;

      setEntrepriseId(entreprise.id);

      // Charger les chantiers avec métriques temps réel au lieu de calculs manuels
      const { data } = await supabase
        .from("chantiers")
        .select(`
          *,
          chantier_metrics_realtime (metrics),
          devis (montant_ht, montant_ttc, tva, actif),
          factures_fournisseurs (*),
          equipe_chantier (
            membre_id,
            membres_equipe (*)
          ),
          frais_chantier (*)
        `)
        .eq("entreprise_id", entreprise.id)
        .order("created_at", { ascending: false });

      // Charger les affectations de retard pour tous les chantiers
      const chantierIds = (data || []).map(c => c.id);
      let retardAffectations: any[] | null = null;

      // Ne faire la requête que si on a des chantiers
      if (chantierIds.length > 0) {
        const { data: affData } = await supabase
          .from('affectations_chantiers')
          .select('chantier_id, date_fin, jours_travailles, jours_details')
          .in('chantier_id', chantierIds)
          .like('notes', 'Retard chantier%');
        retardAffectations = affData;
      }

      // Grouper les affectations de retard par chantier
      const retardParChantier: Record<string, { dateFinRetard: string; joursRetard: number }> = {};
      if (retardAffectations && retardAffectations.length > 0) {
        // Regrouper par chantier_id
        const affParChantier: Record<string, typeof retardAffectations> = {};
        for (const aff of retardAffectations) {
          if (!affParChantier[aff.chantier_id]) {
            affParChantier[aff.chantier_id] = [];
          }
          affParChantier[aff.chantier_id].push(aff);
        }

        // Calculer dateFinRetard et joursRetard pour chaque chantier
        for (const [chantierId, affs] of Object.entries(affParChantier)) {
          // dateFinRetard = max date_fin des affectations retard
          const dateFinRetard = affs.reduce((max, a) => a.date_fin > max ? a.date_fin : max, affs[0].date_fin);

          // joursRetard = comptage des jours uniques via jours_details
          const uniqueDelayDays = new Set<string>();
          for (const aff of affs) {
            const joursDetails = aff.jours_details as { date: string; period: string }[] | null;
            if (joursDetails && Array.isArray(joursDetails)) {
              for (const detail of joursDetails) {
                uniqueDelayDays.add(`${detail.date}:${detail.period}`);
              }
            }
          }

          let joursRetard = 0;
          if (uniqueDelayDays.size > 0) {
            for (const key of uniqueDelayDays) {
              const period = key.split(':')[1];
              joursRetard += period === 'FULL' ? 1 : 0.5;
            }
          } else {
            // Fallback: max jours_travailles
            joursRetard = Math.max(...affs.map(a => a.jours_travailles || 0), 0);
          }

          retardParChantier[chantierId] = { dateFinRetard, joursRetard };
        }
      }

      // Map les métriques temps réel et ajouter les infos de retard
      const chantiersWithMetrics = (data || []).map(chantier => ({
        ...chantier,
        metrics: (chantier as any).chantier_metrics_realtime?.metrics as any,
        dateFinRetard: retardParChantier[chantier.id]?.dateFinRetard,
        jours_retard: retardParChantier[chantier.id]?.joursRetard || 0,
      }));

      // Debug: afficher les infos de retard
      if (import.meta.env.DEV) {
        console.log('[Reports] Affectations retard trouvées:', retardAffectations?.length || 0);
        console.log('[Reports] Retard par chantier:', retardParChantier);
        console.log('[Reports] Chantiers avec dateFinRetard:', chantiersWithMetrics.filter(c => c.dateFinRetard).map(c => ({
          nom: c.nom_chantier,
          dateFinRetard: c.dateFinRetard,
          date_fin_initiale: c.date_fin_initiale,
          date_fin_estimee: c.date_fin_estimee,
          jours_retard: c.jours_retard
        })));
      }

      setChantiers(chantiersWithMetrics);
    } catch (error) {
      console.error("Error loading chantiers:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les chantiers par recherche
  const filteredChantiers = chantiers.filter(chantier => 
    chantier.nom_chantier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chantier.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (chantier.adresse || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculer les KPIs
  const chantiersActifs = chantiers.filter(c => 
    c.etat_chantier === 'en_cours' || c.etat_chantier === 'projection'
  ).length;
  const totalChantiers = chantiers.length;
  const rapportsDisponibles = chantiers.filter(c => c.budget_ht > 0).length;
  const totalChantiersNonArchives = chantiers.filter(c => c.etat_chantier !== 'archive').length;

  const handleExportAll = () => {
    toast({
      title: "Export en cours",
      description: "Préparation de l'export de tous les rapports...",
    });
    // L'export individuel est géré par ExportManager dans chaque carte
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div>
        <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
          <FileText className="h-9 w-9 text-primary" aria-hidden="true" />
          Rapports de Chantiers
        </h1>
        <p className="text-muted-foreground mt-2 text-lg">
          Exportez les rapports financiers de vos chantiers.
        </p>
      </div>

      {/* KPI Cards — masqués si < 5 chantiers non-archivés */}
      {totalChantiersNonArchives >= 5 && (
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              Chantiers Actifs
            </CardTitle>
            <TrendingUp className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-white">{chantiersActifs}</div>
            <p className="text-xs text-muted-foreground mt-1">
              En cours d'exécution
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              Total Chantiers
            </CardTitle>
            <FileText className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-white">{totalChantiers}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tous statuts confondus
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-white uppercase">
              Rapports Disponibles
            </CardTitle>
            <FileCheck className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-green-600">{rapportsDisponibles}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Analyses complètes
            </p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" aria-hidden="true" />
        <Input
          placeholder="Rechercher un chantier..."
          className="pl-12 h-12 text-base"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Rechercher un chantier"
          data-miro-target="reports-search-bar"
        />
      </div>

      <div className="grid gap-6">
        {loading ? (
          <Card className="card-premium">
            <CardContent className="pt-16 pb-16 text-center">
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary mb-4" aria-hidden="true" />
              <p className="text-muted-foreground font-medium">Chargement des rapports...</p>
            </CardContent>
          </Card>
        ) : filteredChantiers.length === 0 ? (
          <Card className="card-premium">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-xl font-semibold text-muted-foreground">Aucun chantier trouvé</p>
              <p className="text-sm text-muted-foreground mt-2">Essayez avec d'autres mots-clés</p>
            </CardContent>
          </Card>
        ) : (
          filteredChantiers.map((chantier) => {
            const membres = chantier.equipe_chantier?.map((ec: any) => ec.membres_equipe) || [];
            const devis = chantier.devis?.find((d: any) => d.actif) || chantier.devis?.[0];
            const factures = chantier.factures_fournisseurs || [];
            const frais = chantier.frais_chantier || [];
            const metrics = chantier.metrics;

            // Utiliser les métriques temps réel au lieu de calculs manuels
            const budget_devis = metrics?.budget_ht || 0;
            const cout_journalier_equipe = metrics?.cout_journalier_equipe || 0;
            const budget_disponible = metrics?.budget_disponible || 0;
            const rentabilite_pct = metrics?.marge_finale_pct || 0;
            const jour_critique = metrics?.jour_critique || 0;
            const jours_restants_avant_deficit = metrics?.jours_restants_rentables || 0;
            
            const rentabilityBadge = getRentabilityBadge(rentabilite_pct);
            
            // Seuils harmonisés BTP France (source: calculs-rentabilite.ts)
            // VERT: >= 18% (moyenne BTP), JAUNE: >= 12%, ORANGE: >= 0%, ROUGE: < 0%
            let statut: "success" | "warning" | "alert" | "danger";
            if (rentabilite_pct >= 18) statut = "success";
            else if (rentabilite_pct >= 12) statut = "warning";
            else if (rentabilite_pct >= 0) statut = "alert";
            else statut = "danger";

            // Calcul des coûts pour compatibilité avec ExportManager
            const coutsFixes = (metrics?.couts_fixes_engages || 0);
            const cout_main_oeuvre = (metrics?.cout_main_oeuvre_reel || 0);

            return (
              <Card key={chantier.id} className="card-premium hover-lift">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="text-2xl font-black">{chantier.nom_chantier}</CardTitle>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <CardDescription className="text-base">
                          {chantier.client}
                        </CardDescription>
                        {(chantier.date_debut_prevue || chantier.date_fin_estimee) && (
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            <span className="flex items-center gap-1">
                              {chantier.date_debut_prevue
                                ? new Date(chantier.date_debut_prevue).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                                : '?'
                              }
                              {' → '}
                              {/* Si retard enregistré : afficher date initiale barrée + date de fin retard en orange */}
                              {(() => {
                                // Vérifier si on a un retard enregistré (dateFinRetard existe)
                                if (chantier.dateFinRetard) {
                                  // Utiliser date_fin_initiale si disponible, sinon date_fin_estimee
                                  const dateInitiale = chantier.date_fin_initiale || chantier.date_fin_estimee;
                                  // N'afficher le format barré que si la date initiale est différente de dateFinRetard
                                  if (dateInitiale && dateInitiale !== chantier.dateFinRetard) {
                                    return (
                                      <>
                                        <span className="line-through text-muted-foreground/60">
                                          {new Date(dateInitiale).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                        </span>
                                        <span className="mx-1">→</span>
                                        <span className="text-orange-400 font-semibold">
                                          {new Date(chantier.dateFinRetard).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                                        </span>
                                      </>
                                    );
                                  }
                                }
                                // Pas de retard ou dates identiques : afficher normalement
                                return chantier.date_fin_estimee
                                  ? new Date(chantier.date_fin_estimee).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
                                  : '?';
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div data-miro-target="reports-export-button">
                      <ExportManager
                        chantierId={chantier.id}
                      chantierData={chantier}
                      membres={membres}
                      devis={devis}
                      factures={factures}
                      frais={frais}
                      calculations={{
                        cout_journalier_equipe: cout_journalier_equipe || 0,
                        budget_disponible: budget_disponible || 0,
                        jour_critique: jour_critique || 0,
                        rentabilite_pct: rentabilite_pct || 0,
                        jours_restants_avant_deficit: jours_restants_avant_deficit || 0,
                        statut,
                        // Coût employeur = taux_net × coefficient_charges
                        calculerCoutHoraireReel: (membre: any) => {
                          if (!membre || !membre.taux_horaire) return 0;
                          const coef = membre.coefficient_charges || 1.85;
                          return membre.taux_horaire * coef;
                        },
                        calculerCoutJournalierMembre: (membre: any) => {
                          if (!membre || !membre.taux_horaire) return 0;
                          const coef = membre.coefficient_charges || 1.85;
                          const heures = membre.heures_par_jour || 8;
                          return membre.taux_horaire * coef * heures;
                        },
                      }}
                    />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Budget</p>
                      <p className="text-2xl font-black font-mono text-white">{budget_devis.toFixed(2)} €</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Rentabilité</p>
                      <div className="flex items-center gap-2">
                        <p className={`text-2xl font-black font-mono ${
                          rentabilite_pct >= 18 ? "text-success" :
                          rentabilite_pct >= 12 ? "text-warning" :
                          rentabilite_pct >= 0 ? "text-alert" :
                          "text-danger"
                        }`}>
                          {rentabilite_pct.toFixed(1)} %
                        </p>
                        <Badge className={`${rentabilityBadge.bgColor} ${rentabilityBadge.color} border text-xs`}>
                          {rentabilityBadge.emoji}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">État</p>
                      {(() => {
                        // Vérifier si le chantier est en retard (même logique que ProjectCard)
                        const hasDelayRegistered = (metrics?.cout_retard || 0) > 0;
                        const isDateFinDepassee = chantier.date_fin_estimee
                          ? new Date(chantier.date_fin_estimee) < new Date()
                          : false;
                        const isEnRetard = hasDelayRegistered && isDateFinDepassee && chantier.etat_chantier === 'en_cours';

                        if (isEnRetard) {
                          return (
                            <Badge className="text-sm font-bold px-3 py-1 bg-orange-500/20 text-orange-500 border-orange-500/50">
                              ⏱ En retard
                            </Badge>
                          );
                        }

                        return (
                          <Badge className={`text-sm font-bold px-3 py-1 ${
                            chantier.etat_chantier === 'termine' ? 'bg-green-500/20 text-green-500 border-green-500/50' :
                            chantier.etat_chantier === 'en_cours' ? 'bg-blue-500/20 text-blue-500 border-blue-500/50' :
                            chantier.etat_chantier === 'en_pause' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50' :
                            chantier.etat_chantier === 'annule' ? 'bg-red-500/20 text-red-500 border-red-500/50' :
                            'bg-muted text-muted-foreground border-muted'
                          }`}>
                            {chantier.etat_chantier === 'termine' ? '✓ Terminé' :
                             chantier.etat_chantier === 'en_cours' ? '▶ En cours' :
                             chantier.etat_chantier === 'en_pause' ? '⏸ En pause' :
                             chantier.etat_chantier === 'annule' ? '✕ Annulé' :
                             chantier.etat_chantier === 'projection' ? '📋 Projection' :
                             chantier.etat_chantier || 'Non défini'}
                          </Badge>
                        );
                      })()}
                    </div>
                    {/* Badge textuel rentabilité masqué — redondant avec le % coloré en colonne 2 */}
                    {false && (
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Rentabilité</p>
                      <Badge className={`${rentabilityBadge.bgColor} ${rentabilityBadge.color} border text-sm font-bold px-3 py-1`}>
                        {rentabilityBadge.emoji} {rentabilityBadge.label}
                      </Badge>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Annotations Miro Reports */}
      <MiroAnnotations
        pageId="reports"
        annotations={[
          {
            id: "reports-export",
            targetSelector: "[data-miro-target='reports-export-button']",
            text: "Téléchargez un rapport PDF complet de ce chantier (finances, équipe, rentabilité)",
            position: "left",
            offsetX: -30,
            offsetY: 0,
          },
          {
            id: "reports-search",
            targetSelector: "[data-miro-target='reports-search-bar']",
            text: "Recherchez un chantier par nom, client ou adresse pour accéder rapidement à son rapport",
            position: "bottom",
            offsetX: 0,
            offsetY: 20,
          },
        ]}
      />
    </div>
  );
};

export default Reports;
